import { lazy, Suspense, useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { App } from 'antd'
import { useTranslation } from 'react-i18next'
import { api, baseQty, displayRate, fmt, type Invoke, type Order } from '../api'
import { useSession } from '@argo/common/session'
import { useMediaQuery } from '../lib/useMediaQuery'
import { useDirection } from '../lib/useDirection'
import OrderBook from '../components/OrderBook'
import TradeTape from '../components/TradeTape'
import OrderForm from '../components/OrderForm'
import Sheet from '@argo/common/components/Sheet'

// The charting library is the heaviest dependency in the app and is only
// needed once a market is on screen, so it loads on demand rather than
// sitting in the bundle that renders the login screen.
const PriceChart = lazy(() => import('../components/PriceChart'))
const ChartFallback = () => <div className="panel"><div className="chart-wrap"><div className="chart skeleton" /></div></div>

/** Ticker figures from the recent-trade window the API returns. */
function useStats(trades: { price: string; qty: string }[]) {
  return useMemo(() => {
    const rates = trades.map(x => displayRate(x.price, 'sell')).filter(r => r > 0)
    if (rates.length === 0) return { last: 0, change: 0, high: 0, low: 0, volume: 0 }
    const last = rates[0]
    const first = rates[rates.length - 1]
    return {
      last,
      change: first > 0 ? ((last - first) / first) * 100 : 0,
      high: Math.max(...rates),
      low: Math.min(...rates),
      volume: trades.reduce((s, x) => s + Number(x.qty), 0),
    }
  }, [trades])
}

type MobileTab = 'chart' | 'book' | 'trades'

export default function Exchange() {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const qc = useQueryClient()
  const { account, me } = useSession()
  const desktop = useMediaQuery('(min-width: 900px)')

  const markets = useQuery({ queryKey: ['markets'], queryFn: api.markets })
  // The rate changes rarely and is needed on every order, so it is cached long.
  const fee = useQuery({ queryKey: ['fee'], queryFn: api.fee, staleTime: 300_000 })
  const [picked, setPicked] = useState<string>()
  const list = markets.data?.data ?? [{ id: 0, base: 'AUD', quote: 'MNT' }]
  const market = picked ?? `${list[0].base}/${list[0].quote}`
  const [base, quote] = market.split('/')

  const book = useQuery({ queryKey: ['book', market], queryFn: () => api.book(market), refetchInterval: 4_000 })
  const tape = useQuery({ queryKey: ['trades', market], queryFn: () => api.trades(market), refetchInterval: 4_000 })
  const balances = useQuery({ queryKey: ['balances', account], queryFn: () => api.balances(account), enabled: !!account, refetchInterval: 8_000 })
  const mine = useQuery({ queryKey: ['orders', account], queryFn: () => api.orders(account), enabled: !!account, refetchInterval: 8_000 })

  const trades = useMemo(() => tape.data?.data ?? [], [tape.data])
  const stats = useStats(trades)

  const [side, setSide] = useState<'buy' | 'sell'>('buy')
  const [price, setPrice] = useState('')
  const [tab, setTab] = useState<MobileTab>('chart')
  const [sheet, setSheet] = useState(false)

  // Seed the price from the market once, so the form opens ready to use.
  useEffect(() => {
    if (!price && stats.last > 0) setPrice(String(Math.round(stats.last)))
  }, [stats.last]) // eslint-disable-line react-hooks/exhaustive-deps

  const cancel = useMutation({
    mutationFn: (o: Order) => api.command<Invoke>('tuna', 'cancel', account, {
      k: o.chain_key,
      r1: { x: account, y: o.give_symbol, z: Number(o.qty_remaining), o: '' },
      r2: { x: account, y: o.want_symbol, z: Number(o.price), o: '>=' },
    }),
    onSuccess: () => {
      message.success(t('done'))
      qc.invalidateQueries({ queryKey: ['book', market] })
      qc.invalidateQueries({ queryKey: ['orders', account] })
      qc.invalidateQueries({ queryKey: ['balances', account] })
    },
    onError: (e: Error) => message.error(e.message),
  })

  const pickPrice = (rate: number) => {
    setPrice(String(Math.round(rate)))
    if (!desktop) setSheet(true)
  }

  const form = (
    <OrderForm
      account={account} base={base} quote={quote}
      side={side} onSide={setSide}
      price={price} onPrice={setPrice}
      balances={balances.data?.data ?? []}
      needsPin={me?.pin.set ?? false}
      feeBps={fee.data?.fee_bps ?? 0}
      market={market}
      onPlaced={() => setSheet(false)}
    />
  )

  const openOrders = (
    <div className="panel">
      <div className="panel-head">
        <span className="grow">{t('myOrders')}</span>
        {mine.data?.data.length ? <span className="chip">{mine.data.data.length}</span> : null}
      </div>
      <div className="panel-body flush">
        {(mine.data?.data.length ?? 0) === 0 ? (
          <div className="empty">{t('noOrders')}</div>
        ) : mine.data?.data.map(o => (
          <div className="list-row" key={o.chain_key}>
            <span className={`chip ${o.side === 'buy' ? 'up' : 'down'}`}>{o.side === 'buy' ? t('buy') : t('sell')}</span>
            <div className="grow">
              <div className="title num">{fmt(displayRate(o.price, o.side))}
                <span className="muted" style={{ fontWeight: 400 }}> {o.side === 'sell' ? `${o.give_symbol}/${o.want_symbol}` : `${o.want_symbol}/${o.give_symbol}`}</span>
              </div>
              <div className="sub num">{fmt(baseQty(o), 4)} {o.side === 'sell' ? o.give_symbol : o.want_symbol}</div>
            </div>
            <button className="btn-ghost" disabled={cancel.isPending} onClick={() => cancel.mutate(o)}>{t('cancel')}</button>
          </div>
        ))}
      </div>
    </div>
  )

  const up = stats.change >= 0
  const tick = useDirection(stats.last)

  return (
    <div className="ex">
      {/* ── hero: the price is the loudest thing on the screen ──────────── */}
      <header className="ex-hero panel">
        <div className="ex-hero-main">
          <div className="ex-pairs" role="group" aria-label={t('market')}>
            {list.map(m => {
              const key = `${m.base}/${m.quote}`
              return (
                <button key={key} type="button" className={key === market ? 'on' : ''} aria-pressed={key === market}
                  onClick={() => { setPicked(key); setPrice('') }}>
                  {key}
                </button>
              )
            })}
          </div>
          <div className="ex-price">
            <b className={[stats.last ? (up ? 'up' : 'down') : '', tick ? `flash-${tick}` : ''].filter(Boolean).join(' ')}>
              {stats.last ? fmt(stats.last) : '—'}
            </b>
            <span className={`chip ${stats.last ? (up ? 'up' : 'down') : ''}`}>
              {stats.last ? `${up ? '+' : ''}${stats.change.toFixed(2)}%` : '—'}
            </span>
          </div>
          <span className="muted" style={{ fontSize: 12 }}>{quote} {t('per')} {base}</span>
        </div>

        <dl className="ex-stats">
          <div><dt>{t('high')}</dt><dd className="num">{stats.high ? fmt(stats.high) : '—'}</dd></div>
          <div><dt>{t('low')}</dt><dd className="num">{stats.low ? fmt(stats.low) : '—'}</dd></div>
          <div><dt>{t('volume')} ({base})</dt><dd className="num">{stats.volume ? fmt(stats.volume, 2) : '—'}</dd></div>
          <div><dt>{t('balance')}</dt><dd className="num">{fmt(Number(balances.data?.data.find(b => b.symbol === base)?.amount ?? 0))} {base}</dd></div>
        </dl>
      </header>

      {desktop ? (
        <div className="ex-grid">
          <div className="ex-col-main">
            <Suspense fallback={<ChartFallback />}><PriceChart trades={trades} base={base} quote={quote} /></Suspense>
            <TradeTape trades={trades} base={base} quote={quote} />
          </div>
          <div className="ex-col-book">
            <OrderBook orders={book.data?.data ?? []} base={base} quote={quote} depth={12} lastPrice={stats.last} onPick={pickPrice} />
          </div>
          <div className="ex-col-form">
            <div className="panel">
              <div className="panel-head"><span className="grow">{side === 'buy' ? t('buy') : t('sell')} {base}</span></div>
              <div className="panel-body">{form}</div>
            </div>
            {openOrders}
          </div>
        </div>
      ) : (
        <>
          <div className="ex-tabs" role="tablist">
            {(['chart', 'book', 'trades'] as MobileTab[]).map(x => (
              <button key={x} role="tab" aria-selected={tab === x} className={tab === x ? 'on' : ''} onClick={() => setTab(x)}>
                {t(x)}
              </button>
            ))}
          </div>

          {tab === 'chart' && <Suspense fallback={<ChartFallback />}><PriceChart trades={trades} base={base} quote={quote} /></Suspense>}
          {tab === 'book' && <OrderBook orders={book.data?.data ?? []} base={base} quote={quote} lastPrice={stats.last} onPick={pickPrice} />}
          {tab === 'trades' && <TradeTape trades={trades} base={base} quote={quote} />}

          {openOrders}

          {/* thumb-reachable entry point, the way trading apps do it */}
          <div className="ex-actions">
            <button className="submit buy" onClick={() => { setSide('buy'); setSheet(true) }}>{t('buy')} {base}</button>
            <button className="submit sell" onClick={() => { setSide('sell'); setSheet(true) }}>{t('sell')} {base}</button>
          </div>

          <Sheet open={sheet} title={`${side === 'buy' ? t('buy') : t('sell')} ${base}`} onClose={() => setSheet(false)}>
            {form}
          </Sheet>
        </>
      )}
    </div>
  )
}
